import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Copy, Check, Image as ImageIcon } from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../../components/common/Toast';

export default function AdminMediaPage() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const loadMedia = () => {
    setLoading(true);
    api.get('/upload/media')
      .then((res) => {
        if (res?.success) setMedia(res.media || []);
      })
      .catch((err) => showToast(err.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadMedia();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Faqat rasm fayllari (JPG, PNG, WEBP) qabul qilinadi', 'error');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast('Fayl hajmi 5MB dan oshmasligi kerak', 'error');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/upload', formData);
      if (res?.success) {
        showToast('Rasm muvaffaqiyatli yuklandi!', 'success');
        loadMedia();
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Faylni o‘chirishni tasdiqlaysizmi?')) return;
    try {
      const res = await api.delete(`/upload/media/${id}`);
      if (res?.success) {
        showToast('Fayl o‘chirildi', 'success');
        loadMedia();
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleCopyUrl = (item) => {
    navigator.clipboard.writeText(item.url);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 2000);
    showToast('Havola nusxalandi!', 'info');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-lg font-bold text-slate-900">
            Media Kutubxonasi ({media.length})
          </h2>
          <p className="text-xs text-slate-500">
            Advokatlar, ofislar va xizmatlar uchun yuklangan rasmlar
          </p>
        </div>

        <label className="inline-flex items-center px-4 py-2 text-xs font-semibold text-[#091322] bg-[#c5a880] hover:bg-[#d4b993] rounded shadow-sm transition-colors cursor-pointer">
          <Upload className="w-4 h-4 mr-1.5" />
          <span>{uploading ? 'Yuklanmoqda...' : 'Yangi rasm yuklash'}</span>
          <input
            type="file"
            accept="image/*"
            disabled={uploading}
            onChange={handleFileUpload}
            className="hidden"
          />
        </label>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs p-6">
        {loading ? (
          <div className="py-12 text-center text-slate-400">Yuklanmoqda...</div>
        ) : media.length === 0 ? (
          <div className="py-12 text-center text-slate-400">
            <ImageIcon className="w-12 h-12 mx-auto mb-2 text-slate-300" />
            Hali rasmlar yuklanmagan
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {media.map((item) => (
              <div
                key={item.id}
                className="group relative rounded border border-slate-200 overflow-hidden bg-slate-50 flex flex-col"
              >
                <div className="h-28 overflow-hidden bg-slate-200">
                  <img
                    src={item.url}
                    alt={item.originalName}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                </div>

                <div className="p-2 text-[10px] space-y-1">
                  <div className="font-semibold text-slate-800 truncate" title={item.originalName}>
                    {item.originalName}
                  </div>
                  <div className="text-slate-400">
                    {(item.size / 1024).toFixed(1)} KB
                  </div>

                  <div className="pt-1 flex items-center justify-between border-t border-slate-100">
                    <button
                      onClick={() => handleCopyUrl(item)}
                      className="p-1 text-slate-600 hover:text-slate-900"
                      title="Nusxalash"
                    >
                      {copiedId === item.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-1 text-rose-500 hover:text-rose-700"
                      title="O‘chirish"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
